import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Stethoscope,
} from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { DiscriminatedProviderCuratedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface ProviderCuratedEventProps {
  event: DiscriminatedProviderCuratedEvent;
  matterId: string;
}

export const ProviderCuratedEvent: React.FC<ProviderCuratedEventProps> = ({
  event,
  matterId,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { payload, occurred_at, user, agent } = event;
  const { provider, action, diff } = payload;

  // Get provider name
  const providerName = provider.name || 'Unknown Provider';

  // Get provider type
  const providerType = provider.provider_type
    ? formatProviderType(provider.provider_type)
    : null;

  // Format action as a readable label
  const actionLabel = formatAction(action);

  // Check if there are diff details to show
  const hasDiff = diff != null && Object.keys(diff).length > 0;

  return (
    <EventContainer>
      <EventHeader
        icon={<Stethoscope size={20} />}
        title="Provider Curated"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Provider Name and Type */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium">{providerName}</span>
            {providerType && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{providerType}</Badge>
              </>
            )}
          </div>

          {/* Address */}
          {provider.address && (
            <div className="text-xs">
              <span className="text-slate-600">Address:</span>{' '}
              <span className="text-[11px]">{provider.address}</span>
            </div>
          )}

          {/* Action */}
          <div className="flex items-center gap-2 text-xs mt-2">
            <span className="text-slate-600">Action:</span>
            <Badge variant="secondary">{actionLabel}</Badge>
          </div>

          {/* Collapsible Diff Details */}
          {hasDiff && (
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 mt-1.5">
                {isOpen ? (
                  <ChevronDown size={12} />
                ) : (
                  <ChevronRight size={12} />
                )}
                <span>{isOpen ? 'Hide' : 'Show'} changes</span>
              </CollapsibleTrigger>

              <CollapsibleContent className="mt-2 space-y-1.5 border-l-2 border-slate-200 pl-2.5">
                {Object.entries(diff).map(([key, value]) => (
                  <div key={key} className="text-[11px]">
                    <span className="text-slate-600 font-medium">
                      {formatFieldName(key)}:
                    </span>{' '}
                    <span className="text-slate-700">
                      {formatFieldValue(value)}
                    </span>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/treatment/${provider.id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Provider
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions for formatting
function formatProviderType(type: string): string {
  const types: Record<string, string> = {
    ORGANIZATION: 'Organization',
    PRACTITIONER: 'Practitioner',
  };
  return types[type] || type;
}

function formatAction(action: string): string {
  // Convert snake_case or camelCase to Title Case
  return action
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatFieldName(fieldName: string): string {
  // Convert snake_case or camelCase to Title Case
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatFieldValue(value: any): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : 'None';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}
